import { useCallback, useEffect, useState } from 'react';
import { Modal, Spin, Tag, Tooltip } from 'antd';
import type { AxiosResponse } from 'axios';
import { useLocation } from 'react-router-dom';
import SystemHeader from '@/widgets/layout/SystemHeader';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { useBackToParent, usePermission, usePersistentPageSize, useOfficeReviewStatuses } from '@/hooks';

import { recipientsApi } from '@/services/recipients/api';
import { isHandledAuthError } from '@/services/client';
import { extractBlobErrorMessage } from '@/utils/download';
import type { RecipientResponse } from '@/services/recipients/types';
import { CASE_STATUS_KEYS, CASE_STATUSES, CASE_STATUS_CODE_MAP, type CaseStatus } from '@/constants/enums';
import { RECIPIENT_CSV_LABELS } from '@/data/recipientFields';
import { useToast, OfficeReviewStatusTag } from '@/widgets/common';
import useSearchPersistence from '@/hooks/useSearchPersistence';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { Pagination, BackButton, SearchCard, ResultCard, SortableHeader, TableLoadingRow, DemoInput, DemoSelect, DemoBadge, DownloadIcon, ResizeHandle } from '@/widgets/demo/ListPrimitives';
import { ColumnVisibilityControl } from '@/widgets/demo/ColumnVisibilityControl';
import { demoButtonClass, computePagination, useSortState, useResizableColumns, type ColumnWidthMap } from '@/widgets/demo/listHelpers';
import RecipientBankInfoCsvImportDialog from './RecipientBankInfoCsvImportDialog';

const RECIPIENT_COLUMN_DEFAULTS: ColumnWidthMap = {
  hasComment: 92,
  hasBlankBasicInfo: 124,
  identId: 104,
  importNos: 84,
  householdIdentCode: 128,
  name: 112,
  noticeAddressee: 120,
  address: 124,
  rStatus: 88,
  amount: 76,
  pStatus: 144,
  officeReviewStatus: 192,
  registrant: 60,
  registeredAt: 104,
  updater: 60,
  updatedAtStr: 104,
  action: 184,
};

const RECIPIENT_COLUMN_ORDER = [
  'hasComment',
  'hasBlankBasicInfo',
  'identId',
  'importNos',
  'householdIdentCode',
  'name',
  'noticeAddressee',
  'address',
  'rStatus',
  'amount',
  'pStatus',
  'officeReviewStatus',
  'action',
  'registrant',
  'registeredAt',
  'updater',
  'updatedAtStr',
] as const;

// 列表示設定（No.30）用の画面キーと、列ID→日本語ラベルのマップ。
// ラベルは一覧のヘッダ表示テキストと揃える（SEARCH_FIELD_LABELS 依存分は下で定義後に組み立てる）。
const RECIPIENT_COLUMN_VISIBILITY_KEY = 'recipient-search';

// 操作列(action)は常に表示（受給者詳細/申出書への導線のため非表示にできない）。
// 識別ID(identId)も主キー相当のため常に表示にして、行の同定ができなくなる事故を防ぐ。
const RECIPIENT_ALWAYS_VISIBLE_COLUMNS = ['action', 'identId'];

const WELFARE_STATUS_ALL_VALUE = 'ALL';
const PAYMENT_STATUS_ALL_VALUE = 'ALL';
const EMPTY_DISPLAY = '—';
const SEARCH_FIELD_LABELS = {
  householdIdentCode: RECIPIENT_CSV_LABELS[0] ?? '世帯識別符号',
  name: '世帯主氏名',
  noticeAddressee: RECIPIENT_CSV_LABELS[51] ?? '決定通知書宛名',
  address: RECIPIENT_CSV_LABELS[53] ?? '決定通知書住所',
} as const;

// 決定通知書宛名(f52)列の検索フォームのプレースホルダ。世帯主氏名(f13)が死亡した元世帯主の
// まま残るケースに対応するための専用検索項目（顧客要望 IJ_00013）。検索ラベルは一覧の列見出し
// （SEARCH_FIELD_LABELS.noticeAddressee = 決定通知書宛名）と揃える（顧客要望：表記不一致の解消）。
const NOTICE_ADDRESSEE_SEARCH_LABEL = SEARCH_FIELD_LABELS.noticeAddressee;

// 列表示設定の列ラベル（一覧ヘッダの表示テキストと揃える）。
const RECIPIENT_COLUMN_LABELS: Record<(typeof RECIPIENT_COLUMN_ORDER)[number], string> = {
  hasComment: 'コメント有無',
  hasBlankBasicInfo: '基本情報空欄有無',
  identId: '識別ID',
  importNos: '取込番号',
  householdIdentCode: SEARCH_FIELD_LABELS.householdIdentCode,
  name: SEARCH_FIELD_LABELS.name,
  noticeAddressee: SEARCH_FIELD_LABELS.noticeAddressee,
  address: SEARCH_FIELD_LABELS.address,
  rStatus: '受給状態',
  amount: '給付金額',
  pStatus: 'ステータス',
  officeReviewStatus: '事務局確認用ステータス',
  action: '操作',
  registrant: '登録者',
  registeredAt: '登録時間',
  updater: '更新者',
  updatedAtStr: '更新時間',
};

const RECIPIENT_COLUMN_VISIBILITY_ITEMS = RECIPIENT_COLUMN_ORDER.map((id) => ({
  id,
  label: RECIPIENT_COLUMN_LABELS[id],
}));

const WELFARE_STATUS_OPTIONS = [
  { value: WELFARE_STATUS_ALL_VALUE, label: '受給状態（全て）' },
  { value: '保護受給中', label: '保護受給中' },
  { value: '保護廃止中', label: '保護廃止中' },
] as const;

const PAYMENT_STATUS_OPTIONS = [
  { value: PAYMENT_STATUS_ALL_VALUE, label: 'ステータス（全て）' },
  ...CASE_STATUS_KEYS.map((key) => ({
    value: CASE_STATUSES[key],
    label: CASE_STATUSES[key],
  })),
] as const;

const DEFAULT_PAYMENT_STATUS = PAYMENT_STATUS_ALL_VALUE;

// 事務局確認用ステータス（No.25）絞り込み。ALL=絞り込まない / '0'=未設定(NULL) / 正の値=master.id。
// 選択肢は自治体マスタ（office_review_status_master）から動的取得するため、ここでは値だけを定義する。
const OFFICE_REVIEW_ALL_VALUE = 'ALL';
const OFFICE_REVIEW_UNSET_VALUE = '0';

/** 事務局確認用ステータスの選択値 → サーバ送信値。ALL=undefined / '0'=0(未設定) / id=数値。 */
function officeReviewParam(value: string | undefined): number | undefined {
  if (!value || value === OFFICE_REVIEW_ALL_VALUE) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

// 有無プルダウン（コメント有無 / 基本情報空欄有無）の選択値。品川区 No.11 要望。
// ALL=絞り込まない / PRESENT=有 / ABSENT=−。サーバへは true/false/undefined に変換して渡す。
const PRESENCE_ALL_VALUE = 'ALL';
const PRESENCE_PRESENT_VALUE = 'PRESENT';
const PRESENCE_ABSENT_VALUE = 'ABSENT';

const COMMENT_PRESENCE_OPTIONS = [
  { value: PRESENCE_ALL_VALUE, label: 'コメント（全て）' },
  { value: PRESENCE_PRESENT_VALUE, label: 'コメント：有' },
  { value: PRESENCE_ABSENT_VALUE, label: 'コメント：−' },
] as const;

const BLANK_PRESENCE_OPTIONS = [
  { value: PRESENCE_ALL_VALUE, label: '基本情報空欄（全て）' },
  { value: PRESENCE_PRESENT_VALUE, label: '基本情報空欄：有' },
  { value: PRESENCE_ABSENT_VALUE, label: '基本情報空欄：−' },
] as const;

/** 有無プルダウンの選択値 → サーバ送信値。PRESENT=true / ABSENT=false / ALL=undefined。 */
function presenceParam(value: string): boolean | undefined {
  if (value === PRESENCE_PRESENT_VALUE) return true;
  if (value === PRESENCE_ABSENT_VALUE) return false;
  return undefined;
}

interface SearchFilters {
  identId: string;
  importNo: string;
  nameFilter: string;
  noticeAddresseeFilter: string;
  householdIdentCode: string;
  addressFilter: string;
  welfareUi: string;
  paymentUi: string;
  /** 事務局確認用ステータス絞り込み（No.25）。ALL / '0'=未設定 / master.id。 */
  officeReviewUi: string;
  /** コメント有無プルダウン（PRESENCE_*）。品川区 No.11 要望。 */
  commentUi: string;
  /** 基本情報空欄有無プルダウン（PRESENCE_*）。品川区 No.11 要望。 */
  blankUi: string;
  /**
   * ダッシュボードからの多値ステータス絞り込み用（CaseStatus code CSV）。
   * 単一 select の paymentUi より優先されてバックエンドに `paymentStatuses` として渡る。
   * 通常の検索 UI からは設定されない（URL preset 経由のみ）。
   */
  paymentCodesCsv?: string;
  /** ダッシュボード専用の検索 preset。通常の検索操作では保持しない。 */
  dashboardPreset?: 'overallPending';
  includeDeleted: boolean;
  sortBy?: string | null;
  sortOrder?: string | null;
  page?: number;
}

const DEFAULT_SEARCH_FILTERS: SearchFilters = {
  identId: '',
  importNo: '',
  nameFilter: '',
  noticeAddresseeFilter: '',
  householdIdentCode: '',
  addressFilter: '',
  welfareUi: WELFARE_STATUS_ALL_VALUE,
  paymentUi: DEFAULT_PAYMENT_STATUS,
  officeReviewUi: OFFICE_REVIEW_ALL_VALUE,
  commentUi: PRESENCE_ALL_VALUE,
  blankUi: PRESENCE_ALL_VALUE,
  includeDeleted: false,
  sortBy: null,
  sortOrder: null,
  page: 1,
};

interface RecipientRow {
  id: number;
  identId: string;
  importNos: string[];
  householdIdentCode: string;
  name: string;
  noticeAddressee: string;
  address: string;
  rStatus: string;
  amount: string;
  pStatus: string;
  /** 事務局確認用ステータス（No.25）。master.id または未設定時は null。 */
  officeReviewStatusId: number | null;
  latestApplicationId?: number | null;
  attention?: boolean;
  /** コメント有無列：コメントが非空なら true（有） */
  hasComment: boolean;
  /** コメント本文（コメント有のツールチップ表示用） */
  comment: string;
  /** 基本情報空欄有無列：決定通知書4項目のいずれかが空なら true（有） */
  hasBlankBasicInfo: boolean;
  /** 空欄になっている基本情報項目のラベル一覧（ツールチップ表示用） */
  blankBasicInfoFields: string[];
  registeredBy: string;
  updatedBy: string;
  registeredAt: string;
  updatedAtStr: string;
  deleted: boolean;
}

/** 決定通知書4項目（受給者詳細「基本情報」基準）。いずれか空欄なら「基本情報空欄有無＝有」とする。 */
const BASIC_INFO_BLANK_TARGETS: { label: string; pick: (r: RecipientResponse) => string | null | undefined }[] = [
  { label: '世帯主氏名', pick: (r) => r.name },
  { label: '決定通知書宛名', pick: (r) => r.householdFields?.f52 },
  { label: '郵便番号', pick: (r) => r.householdFields?.f53 },
  { label: '住所', pick: (r) => r.householdFields?.f54 },
];

function isBlankValue(v: string | null | undefined): boolean {
  return v == null || String(v).trim() === '';
}

function resolveDisplayedPaymentStatus(r: RecipientResponse): string {
  const rawStatus = r.paymentState?.trim();
  if (rawStatus && rawStatus !== EMPTY_DISPLAY && rawStatus !== "ー") {
    return rawStatus;
  }
  // ステータス未設定（旧 INITIAL "ー" 相当）の場合のデフォルト表示：
  // 保護受給中は申出書を経由せず「給付額算出対象作成待」へ進む。
  // 保護廃止中は識別ID出力 → 申出書 → 承認後に給付額算出対象作成待。
  return r.welfareStatus === "保護廃止中"
    ? CASE_STATUSES.IDENT_ID_UNEXPORTED
    : CASE_STATUSES.AWAITING_TRANSFER_LIST;
}

function formatTimestamp(ts: string | undefined | null): string {
  if (!ts) return EMPTY_DISPLAY;
  const d = new Date(ts);
  if (isNaN(d.getTime())) return EMPTY_DISPLAY;
  const yyyy = d.getFullYear();
  const MM = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const HH = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${yyyy}-${MM}-${dd} ${HH}:${mm}:${ss}`;
}

function mapRecipient(r: RecipientResponse): RecipientRow {
  const attention =
    r.attentionRequired === true ||
    Boolean(
      r.comment && (r.comment.includes("要注意") || r.comment.includes("注意")),
    );
  const amount =
    r.benefitAmount != null && !Number.isNaN(Number(r.benefitAmount))
      ? `${Number(r.benefitAmount).toLocaleString()}円`
      : EMPTY_DISPLAY;
  const importNosArr =
    r.importNos && r.importNos.trim().length > 0
      ? r.importNos
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : r.importNo
        ? [r.importNo]
        : [];

  const comment = r.comment?.trim() ?? "";
  const blankBasicInfoFields = BASIC_INFO_BLANK_TARGETS.filter((t) =>
    isBlankValue(t.pick(r)),
  ).map((t) => t.label);

  return {
    id: r.id,
    identId: r.identId,
    importNos: importNosArr,
    householdIdentCode: r.householdIdentCode || EMPTY_DISPLAY,
    name: r.name || EMPTY_DISPLAY,
    noticeAddressee: r.householdFields?.f52 || EMPTY_DISPLAY,
    address: r.address || EMPTY_DISPLAY,
    rStatus: r.welfareStatus || EMPTY_DISPLAY,
    amount,
    pStatus: resolveDisplayedPaymentStatus(r),
    officeReviewStatusId: r.officeReviewStatusId ?? null,
    latestApplicationId: r.latestApplicationId ?? null,
    attention,
    hasComment: comment.length > 0,
    comment,
    hasBlankBasicInfo: blankBasicInfoFields.length > 0,
    blankBasicInfoFields,
    registeredBy: r.registeredBy ?? EMPTY_DISPLAY,
    updatedBy: r.updatedBy ?? EMPTY_DISPLAY,
    registeredAt: formatTimestamp(r.createdAt),
    updatedAtStr: formatTimestamp(r.updatedAt),
    deleted: r.deleted === true,
  };
}

function paymentParam(label: string): string | undefined {
  if (!label || label === PAYMENT_STATUS_ALL_VALUE) return undefined;
  return label;
}

function welfareParam(label: string): string | undefined {
  if (!label || label === WELFARE_STATUS_ALL_VALUE) return undefined;
  return label;
}

/**
 * ダッシュボードからの URL クエリ `?welfare=...&statuses=...&dashboardPreset=...` を SearchFilters に反映する。
 *
 * ダッシュボードカードのクリック経由（URL に dashboardPreset / welfare / statuses のいずれかが付く）
 * を検出した場合は、localStorage に保存されている前回検索条件 `saved` を完全に破棄し、
 * カードが意図する条件だけを反映する。これにより：
 *   - 「①受給中 → ②戻る → ③全体」で前画面の条件が残る事象
 *   - 「⑤保護廃止」のカード件数とリスト件数が、前回 paymentUi の残留で食い違う事象
 * を同時に解消する。
 *
 * 受給者管理メニューや一覧詳細からの戻りなど、URL に preset が無い経路では従来通り
 * `saved` を尊重して前回検索状態を復元する。
 *
 * - welfare=active → 「保護受給中」 / welfare=ended → 「保護廃止中」 / welfare=all → 「全て」
 * - statuses はカンマ区切りの CaseStatus code（例: "12,13,15"）。
 *   複数指定があれば paymentCodesCsv にセットし、UI の単一 select は「全て」に。
 *   単一指定なら従来の paymentUi（displayName）にも反映して UI に表示する。
 *   statuses 未指定（targetCard 等）は paymentUi を「全て」にする。
 */
function applyUrlPreset(search: string, saved: SearchFilters): SearchFilters {
  if (!search) return saved;
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const dashboardPreset = params.get('dashboardPreset');
  const welfare = params.get('welfare');
  const statusesRaw = params.get('statuses');
  // No.27: ダッシュボード「受給中世帯」内訳カードからの遷移。'0'=未設定、正の値=master.id。
  const officeReviewRaw = params.get('officeReviewStatusId');
  if (!dashboardPreset && !welfare && !statusesRaw && !officeReviewRaw) return saved;

  // ダッシュボード経由の遷移は前回検索条件を完全リセットして preset の意味そのままを表示する。
  // paymentUi は DEFAULT_SEARCH_FILTERS の「給付額算出対象作成待」ではなく「全て」を初期値にする
  // （ダッシュボードのカード件数は payment_status 絞り込みなしの集計が大半のため）。
  const next: SearchFilters = {
    ...DEFAULT_SEARCH_FILTERS,
    paymentUi: PAYMENT_STATUS_ALL_VALUE,
  };
  if (dashboardPreset === 'overallPending') {
    next.dashboardPreset = 'overallPending';
  }
  if (welfare === 'active') next.welfareUi = '保護受給中';
  else if (welfare === 'ended') next.welfareUi = '保護廃止中';
  else if (welfare === 'all') next.welfareUi = WELFARE_STATUS_ALL_VALUE;

  if (statusesRaw) {
    const codes = statusesRaw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (codes.length > 1) {
      next.paymentCodesCsv = codes.join(',');
      next.paymentUi = PAYMENT_STATUS_ALL_VALUE;
    } else if (codes.length === 1) {
      const code = Number(codes[0]);
      const entry = (Object.entries(CASE_STATUS_CODE_MAP) as [CaseStatus, number][]).find(
        ([, c]) => c === code,
      );
      next.paymentUi = entry ? CASE_STATUSES[entry[0]] : PAYMENT_STATUS_ALL_VALUE;
    }
  }
  if (officeReviewRaw != null && officeReviewRaw !== '') {
    const n = Number(officeReviewRaw);
    if (Number.isFinite(n) && n >= 0) {
      next.officeReviewUi = String(n);
    }
  }
  next.page = 1;
  return next;
}

/**
 * コメント有無 / 基本情報空欄有無 列の有・−バッジ。
 * 表記は品川区 No.11 規格に合わせ「有 / −」（無ではなく −）。検索プルダウン（コメント：−／
 * 基本情報空欄：−）とも揃える。
 * 「ぱっと見判断」要望に応えて色分けする：
 * - tone='comment'（コメント有無）: 有=青で強調 / −=淡いグレー
 * - tone='blank'（基本情報空欄有無）: 有=赤で警告 / −=緑でOK
 */
function PresenceTag({ present, tone }: { present: boolean; tone: 'comment' | 'blank' }) {
  let cls: string;
  if (tone === 'comment') {
    cls = present
      ? 'bg-blue-100 text-blue-700 ring-1 ring-inset ring-blue-200'
      : 'bg-gray-100 text-gray-400';
  } else {
    cls = present
      ? 'bg-red-100 text-red-700 ring-1 ring-inset ring-red-200'
      : 'bg-green-100 text-green-700 ring-1 ring-inset ring-green-200';
  }
  return (
    <span
      className={`inline-flex min-w-[2.25rem] items-center justify-center rounded-full px-2.5 py-0.5 text-xs font-bold ${cls}`}
    >
      {present ? '有' : '−'}
    </span>
  );
}

const RecipientSearch = () => {
  const navigate = useAppNavigate();
  const location = useLocation();
  const goBackToParent = useBackToParent();
  const toast = useToast();
  const { hasPermission } = usePermission();
  // No.25: 事務局確認用ステータスの絞り込み選択肢（自治体マスタから動的取得）。
  const { statuses: officeReviewStatuses } = useOfficeReviewStatuses();

  const [savedSearch, setSavedSearch] = useSearchPersistence<SearchFilters>('recipient-search', DEFAULT_SEARCH_FILTERS);

  // 初期マウント時のみ URL preset を適用（リロード時の永続化を尊重するため再適用しない）。
  // 前回の検索条件は常に保持する（ツール共通仕様）。
  // 旧バージョンで localStorage に永続化された検索条件には noticeAddresseeFilter 等の
  // 新規項目が欠けるため、DEFAULT_SEARCH_FILTERS で補完してから適用する
  // （applied.xxx.trim() が undefined を踏んで検索が落ちるのを防ぐ）。
  // 初期検索条件はマウント時に一度だけ算出して固定する（描画中の ref 参照を避けるため useState 遅延初期化）。
  const [initialSearch] = useState<SearchFilters>(
    () => ({ ...DEFAULT_SEARCH_FILTERS, ...applyUrlPreset(location.search, savedSearch) }),
  );

  const [identId, setIdentId] = useState(initialSearch.identId);
  const [importNo, setImportNo] = useState(initialSearch.importNo);
  const [nameFilter, setNameFilter] = useState(initialSearch.nameFilter);
  const [noticeAddresseeFilter, setNoticeAddresseeFilter] = useState(initialSearch.noticeAddresseeFilter);
  const [householdIdentCode, setHouseholdIdentCode] = useState(initialSearch.householdIdentCode);
  const [addressFilter, setAddressFilter] = useState(initialSearch.addressFilter);
  const [welfareUi, setWelfareUi] = useState(initialSearch.welfareUi);
  const [paymentUi, setPaymentUi] = useState(initialSearch.paymentUi);
  const [officeReviewUi, setOfficeReviewUi] = useState(initialSearch.officeReviewUi ?? OFFICE_REVIEW_ALL_VALUE);
  const [commentUi, setCommentUi] = useState(initialSearch.commentUi ?? PRESENCE_ALL_VALUE);
  const [blankUi, setBlankUi] = useState(initialSearch.blankUi ?? PRESENCE_ALL_VALUE);
  const [includeDeleted, setIncludeDeleted] = useState(initialSearch.includeDeleted ?? false);

  const [applied, setApplied] = useState<SearchFilters>(initialSearch);

  const [rows, setRows] = useState<RecipientRow[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(initialSearch.page ?? 1);
  const [pageSize, setPageSize] = usePersistentPageSize('recipient-search');
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState<string | null>(null);
  const [bankInfoCsvOpen, setBankInfoCsvOpen] = useState(false);
  const { sort, setSort, sortQuery } = useSortState(
    initialSearch.sortBy ? { sortBy: initialSearch.sortBy, order: initialSearch.sortOrder as 'asc' | 'desc' | null } : undefined,
  );
  const { widths: colWidths, startResize } = useResizableColumns({ defaults: RECIPIENT_COLUMN_DEFAULTS });

  // 列表示設定（No.30）。表示中の列だけに絞った順序配列を導出し、colgroup/thead/tbody/幅計算を統一する。
  const { isVisible: isColVisible, setVisible: setColVisible, reset: resetCols, hiddenCount: hiddenColCount } =
    useColumnVisibility(RECIPIENT_COLUMN_VISIBILITY_KEY, RECIPIENT_COLUMN_ORDER, {
      alwaysVisible: RECIPIENT_ALWAYS_VISIBLE_COLUMNS,
    });
  const visibleColumnOrder = RECIPIENT_COLUMN_ORDER.filter((id) => isColVisible(id));
  // 隠した列は colWidths から除外して合計幅を出す（隙間/幅ズレ防止）。
  const tableWidth = visibleColumnOrder.reduce((sum, id) => sum + (colWidths[id] ?? 0), 0);
  // tbody の空行/ローディング行 colSpan は「表示中の列数 + 末尾の余白 col(1)」。
  const visibleColSpan = visibleColumnOrder.length + 1;
  const handleSortChange = (next: typeof sort) => {
    setSort(next);
    setPage(1);
  };

  const fetchPage = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await recipientsApi.list({
        identId: applied.identId.trim() || undefined,
        importNo: applied.importNo.trim() || undefined,
        householdIdentCode: applied.householdIdentCode.trim() || undefined,
        name: applied.nameFilter.trim() || undefined,
        noticeAddressee: applied.noticeAddresseeFilter.trim() || undefined,
        address: applied.addressFilter.trim() || undefined,
        welfareStatus: welfareParam(applied.welfareUi),
        paymentStatus: paymentParam(applied.paymentUi),
        officeReviewStatusId: officeReviewParam(applied.officeReviewUi),
        paymentStatuses: applied.paymentCodesCsv || undefined,
        dashboardPreset: applied.dashboardPreset,
        hasComment: presenceParam(applied.commentUi ?? PRESENCE_ALL_VALUE),
        basicInfoBlank: presenceParam(applied.blankUi ?? PRESENCE_ALL_VALUE),
        excludeDeleted: applied.includeDeleted ? undefined : true,
        ...sortQuery,
        page: page - 1,
        size: pageSize,
      });
      // 取得ページが範囲外（永続化された page が絞り込み後の総ページ数を超える等）で
      // 0件返却された場合、該当データはあるのに「データなし」表示になる。最終ページへ寄せて再取得する。
      if (data.content.length === 0 && data.totalElements > 0 && page > 1) {
        const lastPage = Math.max(1, Math.ceil(data.totalElements / pageSize));
        setTotalElements(data.totalElements);
        setPage(lastPage);
        return;
      }
      setRows(data.content.map(mapRecipient));
      setTotalElements(data.totalElements);
    } catch (e) {
      setRows([]);
      setTotalElements(0);
      if (isHandledAuthError(e)) return;
    } finally {
      setLoading(false);
    }
  }, [applied, page, pageSize, sortQuery]);

  useEffect(() => {
    // 検索条件・ページ・並び順の変更に応じてサーバ取得する正当なデータ取得。
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchPage();
  }, [fetchPage]);

  useEffect(() => {
    // 並び順・ページ変更時のみ前回検索条件を保存する（applied 自体は runSearch で保存済み）。
    setSavedSearch({ ...applied, sortBy: sort.sortBy, sortOrder: sort.order, page });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort.sortBy, sort.order, page]);

  const runSearch = () => {
    setLoading(true);
    const searchValues: SearchFilters = {
      identId,
      importNo,
      nameFilter,
      noticeAddresseeFilter,
      householdIdentCode,
      addressFilter,
      welfareUi,
      paymentUi,
      officeReviewUi,
      commentUi,
      blankUi,
      includeDeleted,
      sortBy: sort.sortBy,
      sortOrder: sort.order,
      page: 1,
    };
    setApplied(searchValues);
    setSavedSearch(searchValues);
    setPage(1);
  };

  const runClear = () => {
    setLoading(true);
    setIdentId('');
    setImportNo('');
    setNameFilter('');
    setNoticeAddresseeFilter('');
    setHouseholdIdentCode('');
    setAddressFilter('');
    setWelfareUi(WELFARE_STATUS_ALL_VALUE);
    setPaymentUi(DEFAULT_PAYMENT_STATUS);
    setOfficeReviewUi(OFFICE_REVIEW_ALL_VALUE);
    setCommentUi(PRESENCE_ALL_VALUE);
    setBlankUi(PRESENCE_ALL_VALUE);
    setIncludeDeleted(false);
    setSort({ sortBy: null, order: null });
    setApplied(DEFAULT_SEARCH_FILTERS);
    setSavedSearch(DEFAULT_SEARCH_FILTERS);
    setPage(1);
  };

  const stamp = () =>
    new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "");

  const exportParams = () => ({
    identId: applied.identId.trim() || undefined,
    importNo: applied.importNo.trim() || undefined,
    householdIdentCode: applied.householdIdentCode.trim() || undefined,
    name: applied.nameFilter.trim() || undefined,
    noticeAddressee: applied.noticeAddresseeFilter.trim() || undefined,
    address: applied.addressFilter.trim() || undefined,
    welfareStatus: welfareParam(applied.welfareUi),
    paymentStatus: paymentParam(applied.paymentUi),
    paymentStatuses: applied.paymentCodesCsv || undefined,
    dashboardPreset: applied.dashboardPreset,
    excludeDeleted: applied.includeDeleted ? undefined : true,
  });

  const downloadBlob = (data: BlobPart, filename: string) => {
    const blob = new Blob([data], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const downloadFile = (data: BlobPart, filename: string, contentType: string) => {
    const blob = new Blob([data], { type: contentType });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const resolveFilename = (response: AxiosResponse<Blob>, fallback: string): string => {
    const contentDisposition = response.headers['content-disposition'];
    if (typeof contentDisposition !== 'string') {
      return fallback;
    }

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      try {
        return decodeURIComponent(utf8Match[1]);
      } catch {
        return utf8Match[1];
      }
    }

    const simpleMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
    return simpleMatch?.[1] ?? fallback;
  };

  const downloadIdentIdListCsv = async () => {
    setDownloadLoading('識別IDを出力しています...');
    try {
      const res = await recipientsApi.exportIdentIdsCsv(exportParams());
      // 出力件数ヘッダ（x-row-count）が 0 ならヘッダのみ → ダウンロードせず案内する。
      // ヘッダ未送出（undefined）/空文字の場合は従来どおりダウンロードへフォールバックする。
      // 空文字・空白は Number('')===0 で 0 に化けるため、非空の数値文字列のときだけ 0 判定する。
      const rowCount = res.headers['x-row-count'];
      if (typeof rowCount === 'string' && rowCount.trim() !== '' && Number(rowCount) === 0) {
        toast.warning('出力対象のデータがありませんでした。');
        return;
      }
      downloadBlob(res.data, `識別ID_${stamp()}.csv`);
      toast.success("識別IDCSVを出力しました。");
      toast.info('ステータスを更新する場合は、画面をリロードしてください。');
    } catch (e) {
      if (isHandledAuthError(e)) return;
      const message = await extractBlobErrorMessage(e);
      Modal.error({ title: 'エラー', content: message ?? '識別IDの出力に失敗しました' });
    } finally {
      setDownloadLoading(null);
    }
  };

  const downloadProgressListExcel = async () => {
    setDownloadLoading('進捗一覧Excelを出力しています...');
    try {
      const res = await recipientsApi.exportProgressExcel(exportParams());
      // 出力件数ヘッダ（x-record-count）が 0 ならテンプレのみ → ダウンロードせず案内する。
      // ヘッダ未送出（undefined）/空文字の場合は従来どおりダウンロードへフォールバックする。
      // 空文字・空白は Number('')===0 で 0 に化けるため、非空の数値文字列のときだけ 0 判定する。
      const recordCount = res.headers['x-record-count'];
      if (typeof recordCount === 'string' && recordCount.trim() !== '' && Number(recordCount) === 0) {
        toast.warning('出力対象のデータがありませんでした。');
        return;
      }
      downloadFile(
        res.data,
        resolveFilename(res, `進捗一覧_${stamp()}.xlsx`),
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      );
      toast.success("進捗一覧Excelを出力しました。");
    } catch (e) {
      if (isHandledAuthError(e)) return;
      const message = await extractBlobErrorMessage(e);
      Modal.error({ title: 'エラー', content: message ?? '進捗一覧Excelの出力に失敗しました' });
    } finally {
      setDownloadLoading(null);
    }
  };

  const downloadRecipientCsv = async () => {
    setDownloadLoading('受給者一覧CSVを出力しています...');
    try {
      const res = await recipientsApi.exportRecipientsCsv(exportParams());
      // 出力件数ヘッダ（x-row-count）が 0 ならヘッダのみ → ダウンロードせず案内する。
      // ヘッダ未送出（undefined）/空文字の場合は従来どおりダウンロードへフォールバックする。
      // 空文字・空白は Number('')===0 で 0 に化けるため、非空の数値文字列のときだけ 0 判定する。
      const rowCount = res.headers['x-row-count'];
      if (typeof rowCount === 'string' && rowCount.trim() !== '' && Number(rowCount) === 0) {
        toast.warning('出力対象のデータがありませんでした。');
        return;
      }
      downloadBlob(res.data, `受給者一覧_${stamp()}.csv`);
      toast.success("受給者一覧CSVを出力しました。");
    } catch (e) {
      if (isHandledAuthError(e)) return;
      const message = await extractBlobErrorMessage(e);
      Modal.error({ title: 'エラー', content: message ?? '受給者一覧CSVの出力に失敗しました' });
    } finally {
      setDownloadLoading(null);
    }
  };

  // 国計算ツール出力：検索条件に合致する決定通知書作成済世帯の厚労省ツール xlsm を ZIP で一括DL。
  // 対象世帯はバックエンドで絞り込む（NOTICE_PRINTED 以降・原告除外）ため、出力 0 件は toast.warning で案内する。
  const downloadResultSheets = async () => {
    // 国計算ツールは世帯ごとに S3 から xlsm を取得するため、対象が多いと重い。
    // 検索結果が1000世帯を超える場合は、出力前に絞り込みを促す（顧客要望・他の上限と同様にポップアップ）。
    if (totalElements > 1000) {
      Modal.error({ content: '出力対象が1,000世帯を超えているため、出力できません。検索条件で絞り込んでください。' });
      return;
    }
    setDownloadLoading('国計算ツールを出力しています...');
    try {
      const res = await recipientsApi.exportResultSheets(exportParams());
      const sheetCount = Number(res.headers['x-sheet-count'] ?? 0);
      const missingCount = Number(res.headers['x-missing-count'] ?? 0);

      if (sheetCount > 0) {
        downloadFile(res.data, resolveFilename(res, `結果表_${stamp()}.zip`), 'application/zip');
      }

      const parts: string[] = [];
      parts.push(sheetCount > 0 ? `xlsm: ${sheetCount}件` : '出力対象の結果表はありませんでした');
      if (missingCount > 0) parts.push(`未生成世帯: ${missingCount}件`);
      const message = `国計算ツールを出力しました。（${parts.join(' / ')}）`;
      if (missingCount > 0 || sheetCount === 0) {
        toast.warning(message);
      } else {
        toast.success(message);
      }
    } catch (e) {
      if (isHandledAuthError(e)) return;
      // 1000世帯超エラー（HTTP 400）などのバックエンドメッセージを blob 本文から取り出して表示する。
      const message = await extractBlobErrorMessage(e);
      Modal.error({ title: 'エラー', content: message ?? '国計算ツールの出力に失敗しました' });
    } finally {
      setDownloadLoading(null);
    }
  };

  const { totalPages, safePage, rangeFrom, rangeTo } = computePagination(
    page,
    totalElements,
    pageSize,
  );

  return (
    <div className="min-h-screen bg-gray-100">
      {downloadLoading && <Spin fullscreen tip={downloadLoading} />}
      <SystemHeader breadcrumbs={[{ label: "受給者管理" }]} />
      <div className="mx-auto w-full max-w-[1600px] px-6 py-8">
        <BackButton onClick={goBackToParent} />

        <SearchCard>
          {/* 検索条件は一覧の列順（RECIPIENT_COLUMN_ORDER）に合わせて並べる：
              コメント有無 → 基本情報空欄有無 → 識別ID → 取込番号 → 世帯識別符号 →
              世帯主氏名 → 決定通知書宛名 → 決定通知書住所 → 受給状態 → ステータス。
              プルダウン(DemoSelect)も入力(DemoInput)もグリッドセルで全幅に揃う。 */}
          <div className="mb-4 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            <DemoSelect
              value={commentUi}
              onChange={(e) => setCommentUi(e.target.value)}
            >
              {COMMENT_PRESENCE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </DemoSelect>
            <DemoSelect
              value={blankUi}
              onChange={(e) => setBlankUi(e.target.value)}
            >
              {BLANK_PRESENCE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </DemoSelect>
            <DemoInput
              type="text"
              value={identId}
              onChange={(e) => setIdentId(e.target.value)}
              placeholder="識別ID"
            />
            <DemoInput
              type="text"
              value={importNo}
              onChange={(e) => setImportNo(e.target.value)}
              placeholder="取込番号"
            />
            <DemoInput
              type="text"
              value={householdIdentCode}
              onChange={(e) => setHouseholdIdentCode(e.target.value)}
              placeholder={SEARCH_FIELD_LABELS.householdIdentCode}
            />
            <DemoInput
              type="text"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              placeholder={SEARCH_FIELD_LABELS.name}
            />
            <DemoInput
              type="text"
              value={noticeAddresseeFilter}
              onChange={(e) => setNoticeAddresseeFilter(e.target.value)}
              placeholder={NOTICE_ADDRESSEE_SEARCH_LABEL}
            />
            <DemoInput
              type="text"
              value={addressFilter}
              onChange={(e) => setAddressFilter(e.target.value)}
              placeholder={SEARCH_FIELD_LABELS.address}
            />
            <DemoSelect
              value={welfareUi}
              onChange={(e) => setWelfareUi(e.target.value)}
            >
              {WELFARE_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </DemoSelect>
            <DemoSelect
              value={paymentUi}
              onChange={(e) => setPaymentUi(e.target.value)}
            >
              {PAYMENT_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </DemoSelect>
            <DemoSelect
              value={officeReviewUi}
              onChange={(e) => setOfficeReviewUi(e.target.value)}
            >
              <option value={OFFICE_REVIEW_ALL_VALUE}>事務局確認用ステータス（全て）</option>
              <option value={OFFICE_REVIEW_UNSET_VALUE}>事務局確認用ステータス：未設定</option>
              {officeReviewStatuses.map((s) => (
                <option key={s.id} value={String(s.id)}>
                  {s.displayName}
                </option>
              ))}
            </DemoSelect>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm text-gray-700 select-none">
              <input
                type="checkbox"
                checked={includeDeleted}
                onChange={(e) => setIncludeDeleted(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              削除済みを含む
            </label>
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={runClear}
                className={demoButtonClass.secondary}
              >
                クリア
              </button>
              <button
                type="button"
                onClick={runSearch}
                className={demoButtonClass.primary}
              >
                検索
              </button>
            </div>
          </div>
        </SearchCard>

        <ResultCard
          className="mt-6"
          title={
            // 「列表示設定」はタイトル見出しの直後に近接配置する（右端の出力ボタン群とは分ける）。
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-gray-800">検索結果（{totalElements}件）</h2>
              <ColumnVisibilityControl
                columns={RECIPIENT_COLUMN_VISIBILITY_ITEMS}
                isVisible={isColVisible}
                setVisible={setColVisible}
                onReset={resetCols}
                alwaysVisible={RECIPIENT_ALWAYS_VISIBLE_COLUMNS}
                hiddenCount={hiddenColCount}
              />
            </div>
          }
          extra={
            <>
              {hasPermission('RECIPIENT_IMPORT_BANK_CSV') && (
                <button
                  type="button"
                  onClick={() => setBankInfoCsvOpen(true)}
                  className={demoButtonClass.orange}
                >
                  口座情報CSV取込
                </button>
              )}
              {hasPermission('RECIPIENT_EXPORT_IDENT_ID') && (
                <button
                  type="button"
                  onClick={downloadIdentIdListCsv}
                  className={demoButtonClass.darkShrink}
                >
                  <DownloadIcon />
                  識別ID出力
                </button>
              )}
              {hasPermission('RECIPIENT_EXPORT_PROGRESS_CSV') && (
                <button
                  type="button"
                  onClick={downloadProgressListExcel}
                  className={demoButtonClass.darkShrink}
                >
                  進捗一覧Excel出力
                </button>
              )}
              {hasPermission('RECIPIENT_EXPORT_LIST_CSV') && (
                <button
                  type="button"
                  onClick={downloadRecipientCsv}
                  className={demoButtonClass.darkShrink}
                >
                  受給者一覧CSV出力
                </button>
              )}
              {hasPermission('RECIPIENT_EXPORT_LIST_CSV') && (
                <Tooltip title="決定通知書作成済の世帯について、国が提供する計算ツール（厚労省Excel）を一括ダウンロードします">
                  <button
                    type="button"
                    onClick={downloadResultSheets}
                    className={demoButtonClass.darkShrink}
                  >
                    国計算ツール出力
                  </button>
                </Tooltip>
              )}
            </>
          }
        >
          <div className="overflow-x-auto">
            <table
              className="min-w-full table-fixed"
              style={{ width: `${tableWidth}px` }}
            >
              <colgroup>
                {visibleColumnOrder.map((id) => (
                  <col key={id} style={{ width: `${colWidths[id]}px` }} />
                ))}
                <col />
              </colgroup>
              <thead className="border-b-2 border-gray-200 bg-gray-50 whitespace-nowrap">
                <tr>
                  {isColVisible('hasComment') && (
                    <th className="relative px-2 py-3 text-center text-sm font-medium text-gray-700">
                      コメント有無
                      <ResizeHandle
                        columnId="hasComment"
                        onMouseDown={startResize}
                      />
                    </th>
                  )}
                  {isColVisible('hasBlankBasicInfo') && (
                    <th className="relative px-2 py-3 text-center text-sm font-medium text-gray-700">
                      基本情報空欄有無
                      <ResizeHandle
                        columnId="hasBlankBasicInfo"
                        onMouseDown={startResize}
                      />
                    </th>
                  )}
                  {isColVisible('identId') && (
                    <SortableHeader
                      field="identId"
                      label="識別ID"
                      sort={sort}
                      onSortChange={handleSortChange}
                      className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal"
                      trailing={
                        <ResizeHandle
                          columnId="identId"
                          onMouseDown={startResize}
                        />
                      }
                    />
                  )}
                  {isColVisible('importNos') && (
                    <th className="relative px-2 py-3 text-left text-sm font-medium text-gray-700">
                      取込番号
                      <ResizeHandle
                        columnId="importNos"
                        onMouseDown={startResize}
                      />
                    </th>
                  )}
                  {isColVisible('householdIdentCode') && (
                    <SortableHeader
                      field="householdIdentCode"
                      label={SEARCH_FIELD_LABELS.householdIdentCode}
                      sort={sort}
                      onSortChange={handleSortChange}
                      className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal"
                      trailing={
                        <ResizeHandle
                          columnId="householdIdentCode"
                          onMouseDown={startResize}
                        />
                      }
                    />
                  )}
                  {isColVisible('name') && (
                    <SortableHeader
                      field="name"
                      label={SEARCH_FIELD_LABELS.name}
                      sort={sort}
                      onSortChange={handleSortChange}
                      className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal"
                      trailing={
                        <ResizeHandle columnId="name" onMouseDown={startResize} />
                      }
                    />
                  )}
                  {isColVisible('noticeAddressee') && (
                    <th className="relative px-2 py-3 text-left text-sm font-medium text-gray-700">
                      {SEARCH_FIELD_LABELS.noticeAddressee}
                      <ResizeHandle
                        columnId="noticeAddressee"
                        onMouseDown={startResize}
                      />
                    </th>
                  )}
                  {isColVisible('address') && (
                    <SortableHeader
                      field="address"
                      label={SEARCH_FIELD_LABELS.address}
                      sort={sort}
                      onSortChange={handleSortChange}
                      className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal"
                      trailing={
                        <ResizeHandle
                          columnId="address"
                          onMouseDown={startResize}
                        />
                      }
                    />
                  )}
                  {isColVisible('rStatus') && (
                    <th className="relative px-2 py-3 text-left text-sm font-medium text-gray-700">
                      受給状態
                      <ResizeHandle
                        columnId="rStatus"
                        onMouseDown={startResize}
                      />
                    </th>
                  )}
                  {isColVisible('amount') && (
                    <SortableHeader
                      field="benefitAmount"
                      label="給付金額"
                      sort={sort}
                      onSortChange={handleSortChange}
                      className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal"
                      trailing={
                        <ResizeHandle
                          columnId="amount"
                          onMouseDown={startResize}
                        />
                      }
                    />
                  )}
                  {isColVisible('pStatus') && (
                    <SortableHeader
                      field="paymentStatus"
                      label="ステータス"
                      sort={sort}
                      onSortChange={handleSortChange}
                      className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal"
                      trailing={
                        <ResizeHandle
                          columnId="pStatus"
                          onMouseDown={startResize}
                        />
                      }
                    />
                  )}
                  {isColVisible('officeReviewStatus') && (
                    <th className="relative px-2 py-3 text-left text-sm font-medium text-gray-700">
                      事務局確認用ステータス
                      <ResizeHandle
                        columnId="officeReviewStatus"
                        onMouseDown={startResize}
                      />
                    </th>
                  )}
                  {isColVisible('action') && (
                    <th className="px-2 py-3 text-left text-sm font-medium text-gray-700">
                      操作
                    </th>
                  )}
                  {isColVisible('registrant') && (
                    <SortableHeader
                      field="createdBy"
                      label="登録者"
                      sort={sort}
                      onSortChange={handleSortChange}
                      className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal"
                      trailing={
                        <ResizeHandle
                          columnId="registrant"
                          onMouseDown={startResize}
                        />
                      }
                    />
                  )}
                  {isColVisible('registeredAt') && (
                    <SortableHeader
                      field="createdAt"
                      label="登録時間"
                      sort={sort}
                      onSortChange={handleSortChange}
                      className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal"
                      trailing={
                        <ResizeHandle
                          columnId="registeredAt"
                          onMouseDown={startResize}
                        />
                      }
                    />
                  )}
                  {isColVisible('updater') && (
                    <SortableHeader
                      field="updatedBy"
                      label="更新者"
                      sort={sort}
                      onSortChange={handleSortChange}
                      className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal"
                      trailing={
                        <ResizeHandle
                          columnId="updater"
                          onMouseDown={startResize}
                        />
                      }
                    />
                  )}
                  {isColVisible('updatedAtStr') && (
                    <SortableHeader
                      field="updatedAt"
                      label="更新時間"
                      sort={sort}
                      onSortChange={handleSortChange}
                      className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal"
                      trailing={
                        <ResizeHandle
                          columnId="updatedAtStr"
                          onMouseDown={startResize}
                        />
                      }
                    />
                  )}
                  <th aria-hidden="true" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rows.length === 0 ? (
                  loading ? (
                    <TableLoadingRow colSpan={visibleColSpan} />
                  ) : (
                    <tr>
                      <td
                        colSpan={visibleColSpan}
                        className="px-4 py-8 text-center text-sm text-gray-500"
                      >
                        該当するデータがありません
                      </td>
                    </tr>
                  )
                ) : (
                  rows.map((r, index) => (
                    <tr
                      key={`${r.identId}-${index}`}
                      className={r.deleted ? 'bg-gray-200 opacity-60' : 'transition hover:bg-blue-50'}
                    >
                      {isColVisible('hasComment') && (
                        <td className="px-2 py-3 text-center">
                          <PresenceTag present={r.hasComment} tone="comment" />
                        </td>
                      )}
                      {isColVisible('hasBlankBasicInfo') && (
                        <td className="px-2 py-3 text-center">
                          <PresenceTag present={r.hasBlankBasicInfo} tone="blank" />
                        </td>
                      )}
                      {isColVisible('identId') && (
                        <td className="px-2 py-3 text-sm font-mono">
                          <Tooltip title={r.identId} placement="topLeft">
                            <span className="block truncate">{r.identId}</span>
                          </Tooltip>
                        </td>
                      )}
                      {isColVisible('importNos') && (
                        <td className="px-2 py-3 text-sm font-mono">
                          {r.importNos.length === 0 ? (
                            EMPTY_DISPLAY
                          ) : (
                            <Tooltip
                              title={r.importNos.join(", ")}
                              placement="topLeft"
                            >
                              <span className="block truncate">
                                {r.importNos.map((no) => (
                                  <Tag key={no} className="mb-0.5">
                                    {no}
                                  </Tag>
                                ))}
                              </span>
                            </Tooltip>
                          )}
                        </td>
                      )}
                      {isColVisible('householdIdentCode') && (
                        <td className="px-2 py-3 text-sm font-medium">
                          <Tooltip
                            title={r.householdIdentCode}
                            placement="topLeft"
                          >
                            <span className="block truncate">
                              {r.householdIdentCode}
                            </span>
                          </Tooltip>
                        </td>
                      )}
                      {isColVisible('name') && (
                        <td className="px-2 py-3 text-sm">
                          <Tooltip title={r.name} placement="topLeft">
                            <span className="block truncate">{r.name}</span>
                          </Tooltip>
                        </td>
                      )}
                      {isColVisible('noticeAddressee') && (
                        <td className="px-2 py-3 text-sm">
                          {r.noticeAddressee === EMPTY_DISPLAY ? (
                            <span className="text-gray-500">{EMPTY_DISPLAY}</span>
                          ) : (
                            <Tooltip title={r.noticeAddressee} placement="topLeft">
                              <span className="block truncate">{r.noticeAddressee}</span>
                            </Tooltip>
                          )}
                        </td>
                      )}
                      {isColVisible('address') && (
                        <td className="px-2 py-3 text-sm">
                          {r.address === EMPTY_DISPLAY ? (
                            <span className="text-gray-500">{EMPTY_DISPLAY}</span>
                          ) : (
                            <Tooltip title={r.address} placement="topLeft">
                              <span className="block truncate">{r.address}</span>
                            </Tooltip>
                          )}
                        </td>
                      )}
                      {isColVisible('rStatus') && (
                        <td className="min-w-0 px-2 py-3">
                          {r.rStatus === EMPTY_DISPLAY ? (
                            <span className="text-sm text-gray-500">
                              {EMPTY_DISPLAY}
                            </span>
                          ) : (
                            <Tooltip title={r.rStatus} placement="topLeft">
                              <span className="inline-block max-w-full min-w-0 align-middle">
                                <DemoBadge status={r.rStatus} />
                              </span>
                            </Tooltip>
                          )}
                        </td>
                      )}
                      {isColVisible('amount') && (
                        <td
                          className={`px-2 py-3 text-sm truncate ${r.amount === EMPTY_DISPLAY ? "text-center text-gray-500" : "text-left"}`}
                        >
                          {r.amount}
                        </td>
                      )}
                      {isColVisible('pStatus') && (
                        <td className="min-w-0 px-2 py-3">
                          {r.pStatus === EMPTY_DISPLAY ? (
                            <span className="text-sm text-gray-500">
                              {EMPTY_DISPLAY}
                            </span>
                          ) : (
                            <Tooltip title={r.pStatus} placement="topLeft">
                              <span className="inline-block max-w-full min-w-0 align-middle">
                                <DemoBadge status={r.pStatus} />
                              </span>
                            </Tooltip>
                          )}
                        </td>
                      )}
                      {isColVisible('officeReviewStatus') && (
                        <td className="min-w-0 px-2 py-3">
                          <OfficeReviewStatusTag statusId={r.officeReviewStatusId} />
                        </td>
                      )}
                      {isColVisible('action') && (
                      <td className="px-2 py-3 text-left whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() =>
                              navigate("recipient-detail", {
                                recipientId: r.id,
                                detailPageTitle: "受給者情報詳細",
                                baseCrumb: {
                                  label: "受給者管理",
                                  page: "recipient-search",
                                },
                                ...(r.attention
                                  ? {
                                      alert:
                                        "要注意対象です。内容を確認してから対応してください。",
                                    }
                                  : {}),
                              })
                            }
                            className={demoButtonClass.linkBlue}
                          >
                            受給者詳細
                          </button>
                          {r.deleted ? (
                            <span className="text-xs text-gray-400">削除済み</span>
                          ) : (
                            <>
                              {r.latestApplicationId != null
                                ? hasPermission('APPLICATION_VIEW') && (
                                  <button
                                    type="button"
                                    onClick={() => navigate("application-document-detail", {
                                      applicationId: r.latestApplicationId!,
                                    })}
                                    className={demoButtonClass.linkGreen}
                                  >
                                    申出書詳細
                                  </button>
                                )
                                : r.rStatus === "保護廃止中" && hasPermission('APPLICATION_CREATE_MANUAL') && (
                                  <button
                                    type="button"
                                    onClick={() => navigate("application-document-new", {
                                      identId: r.identId,
                                    })}
                                    className={demoButtonClass.linkGreen}
                                  >
                                    申出書詳細
                                  </button>
                                )
                              }
                            </>
                          )}
                        </div>
                      </td>
                      )}
                      {isColVisible('registrant') && (
                        <td className="px-2 py-3 text-sm">
                          <Tooltip title={r.registeredBy} placement="topLeft">
                            <span className="block truncate">
                              {r.registeredBy}
                            </span>
                          </Tooltip>
                        </td>
                      )}
                      {isColVisible('registeredAt') && (
                        <td className="px-2 py-3 text-sm">
                          <Tooltip title={r.registeredAt} placement="topLeft">
                            <span className="block truncate">
                              {r.registeredAt}
                            </span>
                          </Tooltip>
                        </td>
                      )}
                      {isColVisible('updater') && (
                        <td className="px-2 py-3 text-sm">
                          <Tooltip title={r.updatedBy} placement="topLeft">
                            <span className="block truncate">{r.updatedBy}</span>
                          </Tooltip>
                        </td>
                      )}
                      {isColVisible('updatedAtStr') && (
                        <td className="px-2 py-3 text-sm">
                          <Tooltip title={r.updatedAtStr} placement="topLeft">
                            <span className="block truncate">
                              {r.updatedAtStr}
                            </span>
                          </Tooltip>
                        </td>
                      )}
                      <td aria-hidden="true" />
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={safePage}
            totalPages={totalPages}
            total={totalElements}
            rangeFrom={rangeFrom}
            rangeTo={rangeTo}
            onPageChange={(next) => {
              setLoading(true);
              setPage(next);
            }}
            pageSize={pageSize}
            onPageSizeChange={(next) => {
              setLoading(true);
              setPageSize(next);
              setPage(1);
            }}
          />
        </ResultCard>
        <RecipientBankInfoCsvImportDialog
          open={bankInfoCsvOpen}
          onClose={() => setBankInfoCsvOpen(false)}
          onImported={() => void fetchPage()}
        />
      </div>
    </div>
  );
};

export default RecipientSearch;
